import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import { UiTypography } from '@/components/ui/Typography';
import { UiTypographyProps } from '@/components/ui/Typography/types';

import styles from './MessageText.module.scss';

const typographyColorMap: Record<
  'sent' | 'received',
  NonNullable<UiTypographyProps['color']>
> = {
  sent: 'white-static',
  received: 'black-static',
};

const rolePlayColorMap: Record<'sent' | 'received', string> = {
  sent: '#FFFFFF80',
  received: '#0E0D0D80',
};

export const MessageText: React.FC<{
  content: NonNullable<React.ReactNode>;
  variant: 'sent' | 'received';
  show?: boolean;
}> = ({ content, variant, show = true }) => {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = !shouldReduceMotion;

  if (canAnimate) {
    return (
      <AnimatePresence>
        {show && (
          <motion.div
            variants={{
              initial: {
                opacity: 0,
                scale: 0,
              },
              animate: {
                opacity: 1,
                scale: 1,
              },
              exit: {
                opacity: 0,
                scale: 0,
              },
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            className={classNames(styles.message, styles[variant])}
          >
            <Content content={content} variant={variant} />
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <div className={classNames(styles.message, styles[variant])}>
      <Content content={content} variant={variant} />
    </div>
  );
};

function Content({
  content,
  variant,
}: {
  content: NonNullable<React.ReactNode>;
  variant: 'sent' | 'received';
}) {
  const typographyColor = typographyColorMap[variant];
  const rolePlayColor = rolePlayColorMap[variant];

  return (
    <UiTypography
      variant="body-medium"
      component="span"
      color={typographyColor}
    >
      {typeof content === 'string' ? (
        <ReactMarkdown
          components={{
            p: 'span',
            em(props) {
              const { node, ...rest } = props;
              return <i style={{ color: rolePlayColor }} {...rest} />;
            },
          }}
        >
          {content}
        </ReactMarkdown>
      ) : (
        content
      )}
    </UiTypography>
  );
}
